import React, { useState, useEffect, useRef } from 'react';
import { Filter, CheckCircle2, Plus, ChevronDown } from 'lucide-react';

const FilterDropdown = ({ 
    filters, // For promotions
    setFilters, 
    activeTab,
    options, // For generic use
    activeFilter, 
    setActiveFilter,
    t
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const filterRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Safety check for isFiltered
    const isFiltered = filters 
        ? (filters.status !== 'all' || (filters.type && filters.type !== 'all') || (filters.sortBy && filters.sortBy !== 'newest'))
        : (activeFilter && activeFilter !== 'all');

    const handleFilterChange = (key, value) => {
        if (setFilters) {
            setFilters(prev => ({ ...prev, [key]: value }));
        } else if (setActiveFilter) {
            setActiveFilter(value);
            setIsOpen(false); // Auto-close simple filters
        }
    };

    return (
        <div style={{ position: 'relative' }} ref={filterRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex', alignItems: 'center', gap: '10px', height: '46px', padding: '0 24px',
                    borderRadius: '12px',
                    background: isOpen || isFiltered ? 'var(--bg-card)' : 'var(--bg-card)',
                    border: `1px solid ${isOpen || isFiltered ? 'var(--primary)' : 'var(--border)'}`,
                    color: isOpen || isFiltered ? 'var(--primary)' : 'var(--text-main)',
                    boxShadow: isOpen || isFiltered ? '0 4px 12px rgba(var(--primary-rgb), 0.1)' : 'none',
                    fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                }}
            >
                <Filter size={18} />
                <span className="full-text">
                    {options && activeFilter !== 'all' 
                        ? options.find(o => o.id === activeFilter)?.label 
                        : isFiltered ? (t ? t('common.filtered', 'Filtreli') : 'Filtreli') : (t ? t('common.filter', 'Filtrele') : 'Filtrele')}
                </span>
                <ChevronDown 
                    size={16} 
                    style={{ 
                        transition: 'transform 0.2s', 
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                        opacity: 0.7
                    }} 
                />
            </button>

            {isOpen && (
                <div className="fade-in shadow-xl" style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: '12px', zIndex: 1000,
                    width: options ? '220px' : '280px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: '24px', padding: options ? '12px' : '24px', display: 'flex', flexDirection: 'column', gap: options ? '4px' : '20px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
                }}>
                    {/* Generic Options View */}
                    {options ? (
                        options.map(o => (
                            <button 
                                key={o.id} 
                                onClick={() => handleFilterChange('filter', o.id)} 
                                style={{ 
                                    width: '100%', textAlign: 'left', padding: '12px 16px', borderRadius: '14px', 
                                    fontSize: '0.875rem', fontWeight: 600, border: 'none', 
                                    background: activeFilter === o.id ? 'rgba(var(--primary-rgb), 0.1)' : 'transparent', 
                                    color: activeFilter === o.id ? 'var(--primary)' : 'var(--text-main)', 
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                }}
                            >
                                {o.label}
                                {activeFilter === o.id && <CheckCircle2 size={16} />}
                            </button>
                        ))
                    ) : (
                        /* Legacy Specialized View (for Promotions) */
                        <>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', display: 'block', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Durum</label>
                                <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-main)', padding: '4px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                    {[
                                        { id: 'all', label: 'Tümü' },
                                        { id: 'active', label: 'Aktif' },
                                        { id: 'passive', label: 'Pasif' }
                                    ].map(s => (
                                        <button key={s.id} onClick={() => handleFilterChange('status', s.id)} style={{ flex: 1, padding: '6px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700, border: 'none', background: filters?.status === s.id ? 'var(--primary)' : 'transparent', color: filters?.status === s.id ? 'white' : 'var(--text-muted)', cursor: 'pointer', transition: 'all 0.2s' }}>
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {(activeTab === 'discounts' || activeTab === 'coupons') && (
                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', display: 'block', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {activeTab === 'coupons' ? 'Tip' : 'Kampanya Tipi'}
                                    </label>
                                    <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-main)', padding: '4px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                        {[
                                            { id: 'all', label: 'Tümü' },
                                            { id: 'percentage', label: '%' },
                                            { id: 'fixed', label: 'TL' },
                                            { id: 'multiplier', label: 'XP' }
                                        ].map(t => (
                                            <button key={t.id} onClick={() => handleFilterChange('type', t.id)} style={{ flex: 1, padding: '6px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700, border: 'none', background: filters?.type === t.id ? 'var(--primary)' : 'transparent', color: filters?.type === t.id ? 'white' : 'var(--text-muted)', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', display: 'block', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sıralama</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {[
                                        { id: 'newest', label: 'En Yeni' },
                                        { id: 'oldest', label: 'En Eski' },
                                        { id: 'title', label: 'İsme Göre (A-Z)' }
                                    ].map(o => (
                                        <button key={o.id} onClick={() => handleFilterChange('sortBy', o.id)} style={{ width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: '12px', fontSize: '0.875rem', fontWeight: 600, border: 'none', background: filters?.sortBy === o.id ? 'rgba(var(--primary-rgb), 0.08)' : 'transparent', color: filters?.sortBy === o.id ? 'var(--primary)' : 'var(--text-main)', cursor: 'pointer', transition: 'all 0.2s' }}>
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {isFiltered && (
                                <button
                                    onClick={() => setFilters({ status: 'all', type: 'all', target: 'all', sortBy: 'newest' })}
                                    style={{ width: '100%', marginTop: '4px', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-muted)', fontSize: '0.8125rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                                >
                                    Filtreleri Temizle
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default FilterDropdown;